import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Download, Upload, Edit2, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import * as XLSX from "xlsx";
import {
  parseDecimal,
  parseIntPositive,
  padCode,
  parseUf,
  validateRows,
  formatValidationErrors,
  type ValidationError,
} from "../lib/fiscalImportUtils";

const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

export default function ICMS() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isUpdateBulkDialogOpen, setIsUpdateBulkDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [isNatureModalOpen, setIsNatureModalOpen] = useState(false);
  const [natureSearchTerm, setNatureSearchTerm] = useState("");
  const [isEmitenteModalOpen, setIsEmitenteModalOpen] = useState(false);
  const [isBulkNatureModalOpen, setIsBulkNatureModalOpen] = useState(false);
  const [bulkNatureSearchTerm, setBulkNatureSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    ufOrigem: "",
    ufDestino: "",
    aliquota: 0,
    cst: "",
    naturezaOperacaoId: undefined as number | undefined,
    naturezaOperacaoNome: "",
    gatilho: "" as string,
    emitenteId: undefined as number | undefined,
    emitenteNome: "",
  });

  const [bulkUpdateData, setBulkUpdateData] = useState({
    aliquota: undefined as number | undefined,
    naturezaOperacaoId: undefined as number | undefined,
    gatilho: undefined as string | undefined,
  });

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries
  const { data: icmsList = [], refetch } = trpc.icms.list.useQuery();
  const { data: operationNatures = [] } = trpc.operationNatures.list.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const podeSelecionarEmitente = !user?.emitenteId || (emitentes as any[]).length > 1;

  // Mutations
  const createMutation = trpc.icms.create.useMutation({
    onSuccess: () => {
      toast.success("ICMS criado com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar ICMS");
    },
  });

  const createBulkMutation = trpc.icms.createBulk.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} registros importados com sucesso!`);
      setIsBulkDialogOpen(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao importar registros");
    },
  });

  const updateMutation = trpc.icms.update.useMutation({
    onSuccess: () => {
      toast.success("ICMS atualizado com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar ICMS");
    },
  });

  const updateBulkMutation = trpc.icms.updateBulk.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} registros atualizados com sucesso!`);
      setIsUpdateBulkDialogOpen(false);
      setSelectedIds([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar registros");
    },
  });

  const deleteMutation = trpc.icms.delete.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} registro(s) excluído(s) com sucesso!`);
      setSelectedIds([]);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao excluir registros");
    },
  });

  const resetForm = () => {
    setFormData({
      ufOrigem: "",
      ufDestino: "",
      aliquota: 0,
      cst: "",
      naturezaOperacaoId: undefined,
      naturezaOperacaoNome: "",
      gatilho: "",
      emitenteId: undefined,
      emitenteNome: "",
    });
  };

  const handleOpenDialog = (icms?: any) => {
    if (icms) {
      setEditingId(icms.id);
      setFormData({
        ufOrigem: icms.ufOrigem,
        ufDestino: icms.ufDestino,
        aliquota: parseFloat(icms.aliquota),
        cst: icms.cst || "",
        naturezaOperacaoId: icms.naturezaOperacaoId || undefined,
        naturezaOperacaoNome: icms.naturezaOperacaoNome || "",
        gatilho: icms.gatilho || "",
        emitenteId: icms.emitenteId || undefined,
        emitenteNome: icms.emitenteNome || "",
      });
    } else {
      setEditingId(null);
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.ufOrigem || !formData.ufDestino || !formData.cst || !formData.naturezaOperacaoId || !formData.gatilho || (podeSelecionarEmitente && !formData.emitenteId)) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredIcms.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredIcms.map((icms) => icms.id));
    }
  };

  const handleSelectOne = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }

    if (confirm(`Deseja realmente excluir ${selectedIds.length} registro(s)?`)) {
      deleteMutation.mutate({ ids: selectedIds });
    }
  };

  const handleExport = () => {
    const data = filteredIcms.map((icms) => ({
      "UF Origem": icms.ufOrigem,
      "UF Destino": icms.ufDestino,
      "Alíquota": icms.aliquota,
      "CST": icms.cst || "",
      "Natureza ID": icms.naturezaOperacaoId || "",
      "Natureza de Operação": icms.naturezaOperacaoNome || "",
      "Gatilho": icms.gatilho || "",
      "Emitente ID": icms.emitenteId || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ICMS");
    XLSX.writeFile(wb, "icms.xlsx");
    toast.success("Arquivo exportado com sucesso!");
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json: any[] = XLSX.utils.sheet_to_json(worksheet);

        const errors = validateRows(json, (row, i) => {
          const errs: ValidationError[] = [];
          const linha = i + 2;
          if (!parseUf(row["UF Origem"] ?? row["ufOrigem"])) errs.push({ linha, campo: "UF Origem", motivo: "obrigatório, deve ter 2 letras" });
          if (!parseUf(row["UF Destino"] ?? row["ufDestino"])) errs.push({ linha, campo: "UF Destino", motivo: "obrigatório, deve ter 2 letras" });
          if (parseDecimal(row["Alíquota"] ?? row["aliquota"]) === null) errs.push({ linha, campo: "Alíquota", motivo: "obrigatório, deve ser numérico" });
          const cstRaw = row["CST"] ?? row["cst"];
          if (cstRaw !== undefined && cstRaw !== null && cstRaw !== "" && padCode(cstRaw, 2) === null) errs.push({ linha, campo: "CST", motivo: "deve conter até 2 dígitos numéricos" });
          return errs;
        });

        if (errors.length > 0) {
          toast.error(formatValidationErrors(errors), { duration: 8000 });
          return;
        }

        const items = json.map((row) => ({
          ufOrigem: parseUf(row["UF Origem"] ?? row["ufOrigem"]) as string,
          ufDestino: parseUf(row["UF Destino"] ?? row["ufDestino"]) as string,
          aliquota: parseDecimal(row["Alíquota"] ?? row["aliquota"]) as number,
          cst: padCode(row["CST"] ?? row["cst"], 2) ?? "",
          naturezaOperacaoId: parseIntPositive(row["Natureza ID"]) ?? undefined,
          gatilho: row["Gatilho"] ?? row["gatilho"] ?? undefined,
          emitenteId: parseIntPositive(row["Emitente ID"]) ?? undefined,
        }));

        createBulkMutation.mutate({ items });
      } catch (error) {
        toast.error("Erro ao processar arquivo");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleUpdateBulk = () => {
    if (selectedIds.length === 0) {
      toast.error("Selecione pelo menos um registro");
      return;
    }

    if (!bulkUpdateData.aliquota && !bulkUpdateData.naturezaOperacaoId) {
      toast.error("Preencha pelo menos um campo para atualizar");
      return;
    }

    updateBulkMutation.mutate({ ids: selectedIds, ...bulkUpdateData });
  };

  const filteredIcms = icmsList.filter((icms) => {
    const search = searchTerm.toLowerCase();
    return (
      icms.ufOrigem.toLowerCase().includes(search) ||
      icms.ufDestino.toLowerCase().includes(search) ||
      icms.aliquota.toString().includes(search) ||
      (icms.naturezaOperacaoNome && icms.naturezaOperacaoNome.toLowerCase().includes(search))
    );
  });

  if (authLoading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>ICMS</CardTitle>
              <CardDescription>Gerencie as alíquotas de ICMS por UF</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
              <Button variant="outline" size="sm" asChild>
                <label>
                  <Upload className="h-4 w-4 mr-2" />
                  Importar
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
                </label>
              </Button>
              {selectedIds.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setIsUpdateBulkDialogOpen(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Alterar Selecionados
                  </Button>
                  <Button variant="destructive" size="sm" onClick={handleDelete}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir Selecionados
                  </Button>
                </>
              )}
              <Button size="sm" onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Novo ICMS
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Buscar por UF origem, UF destino, alíquota ou natureza..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredIcms.length && filteredIcms.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">UF Origem</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">UF Destino</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Alíquota (%)</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">CST</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Natureza de Operação</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Emitente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Gatilho</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredIcms.map((icms) => (
                  <tr key={icms.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(icms.id)}
                        onChange={() => handleSelectOne(icms.id)}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{icms.ufOrigem}</td>
                    <td className="px-4 py-3 text-sm font-medium">{icms.ufDestino}</td>
                    <td className="px-4 py-3 text-sm">{icms.aliquota}%</td>
                    <td className="px-4 py-3 text-sm font-medium">{icms.cst || "-"}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {icms.naturezaOperacaoNome || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {icms.emitenteNome || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {icms.gatilho || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <Button variant="link" size="sm" onClick={() => handleOpenDialog(icms)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Criação/Edição */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar" : "Novo"} ICMS</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>UF Origem *</Label>
                <Select value={formData.ufOrigem} onValueChange={(value) => setFormData({ ...formData, ufOrigem: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {UFS.map((uf) => (
                      <SelectItem key={uf} value={uf}>
                        {uf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>UF Destino *</Label>
                <Select value={formData.ufDestino} onValueChange={(value) => setFormData({ ...formData, ufDestino: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {UFS.map((uf) => (
                      <SelectItem key={uf} value={uf}>
                        {uf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Alíquota (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.aliquota}
                  onChange={(e) => setFormData({ ...formData, aliquota: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>CST *</Label>
                <Input
                  type="text"
                  maxLength={2}
                  placeholder="ex: 00"
                  value={formData.cst}
                  onChange={(e) => setFormData({ ...formData, cst: e.target.value.replace(/\D/g, '') })}
                />
              </div>
            </div>

            <div>
              <Label>Natureza de Operação *</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.naturezaOperacaoNome}
                  readOnly
                  placeholder="Selecione uma natureza de operação"
                />
                <Button
                  type="button"
                  onClick={() => setIsNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
              </div>
            </div>

            <div>
              <Label>Gatilho *</Label>
              <Select
                value={formData.gatilho || ""}
                onValueChange={(value) => setFormData({ ...formData, gatilho: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consumidor_final">Venda para consumidor final</SelectItem>
                  <SelectItem value="sem_st">Venda sem ST</SelectItem>
                  <SelectItem value="com_st">Venda com ST</SelectItem>
                  <SelectItem value="alc">ALC (Área de Livre Comércio)</SelectItem>
                  <SelectItem value="zona_franca">Zona Franca de Manaus</SelectItem>
                  <SelectItem value="amazonia_ocidental">AO (Amazônia Ocidental)</SelectItem>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {podeSelecionarEmitente && (
              <div>
                <Label>Emitente *</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.emitenteNome}
                    readOnly
                    placeholder="Selecione um emitente"
                  />
                  <Button
                    type="button"
                    onClick={() => setIsEmitenteModalOpen(true)}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Selecionar
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Alteração em Massa */}
      <Dialog open={isUpdateBulkDialogOpen} onOpenChange={setIsUpdateBulkDialogOpen}>
        <DialogContent className="!w-[80vw] !h-[80vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alterar {selectedIds.length} Registro(s) em Massa</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Alíquota (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="Deixe em branco para não alterar"
                value={bulkUpdateData.aliquota || ""}
                onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, aliquota: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </div>

            <div>
              <Label>Natureza de Operação</Label>
              <div className="flex gap-2">
                <Input
                  value={bulkUpdateData.naturezaOperacaoId
                    ? (operationNatures.find((n: any) => n.id === bulkUpdateData.naturezaOperacaoId) as any)?.nome || ""
                    : ""}
                  readOnly
                  placeholder="Não alterar"
                />
                <Button
                  type="button"
                  onClick={() => setIsBulkNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
                {bulkUpdateData.naturezaOperacaoId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBulkUpdateData({ ...bulkUpdateData, naturezaOperacaoId: undefined })}
                  >
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label>Gatilho</Label>
              <Select
                value={bulkUpdateData.gatilho || "unchanged"}
                onValueChange={(value) => setBulkUpdateData({ ...bulkUpdateData, gatilho: value === "unchanged" ? undefined : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Não alterar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unchanged">Não alterar</SelectItem>
                  <SelectItem value="consumidor_final">Venda para consumidor final</SelectItem>
                  <SelectItem value="sem_st">Venda sem ST</SelectItem>
                  <SelectItem value="com_st">Venda com ST</SelectItem>
                  <SelectItem value="alc">ALC (Área de Livre Comércio)</SelectItem>
                  <SelectItem value="zona_franca">Zona Franca de Manaus</SelectItem>
                  <SelectItem value="amazonia_ocidental">AO (Amazônia Ocidental)</SelectItem>
                  <SelectItem value="nenhum">Nenhum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsUpdateBulkDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateBulk}>
              Atualizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal de Seleção de Natureza de Operação — Formulário */}
      <Dialog open={isNatureModalOpen} onOpenChange={setIsNatureModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <Input
            value={natureSearchTerm}
            onChange={(e) => setNatureSearchTerm(e.target.value)}
            placeholder="Buscar por nome, tipo, ID ou emitente..."
            className="mb-3"
          />
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Nome</th>
                  <th className="border-b p-3 text-left">Tipo</th>
                  <th className="border-b p-3 text-left">Emitente</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(operationNatures as any[])
                  .filter((nature: any) =>
                    natureSearchTerm === "" ||
                    nature.nome?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                    nature.tipo?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                    String(nature.id).includes(natureSearchTerm) ||
                    nature.emitenteNome?.toLowerCase().includes(natureSearchTerm.toLowerCase())
                  )
                  .map((nature: any) => (
                    <tr key={nature.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-gray-500 font-mono text-sm">{nature.id}</td>
                      <td className="border-b p-3">{nature.nome}</td>
                      <td className="border-b p-3">{nature.tipo}</td>
                      <td className="border-b p-3 text-sm text-gray-600">{nature.emitenteNome || "-"}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setFormData({ ...formData, naturezaOperacaoId: nature.id, naturezaOperacaoNome: `${nature.nome} — ${nature.tipo}` });
                            setNatureSearchTerm("");
                            setIsNatureModalOpen(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Natureza de Operação — Bulk Update */}
      <Dialog open={isBulkNatureModalOpen} onOpenChange={setIsBulkNatureModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <Input
            value={bulkNatureSearchTerm}
            onChange={(e) => setBulkNatureSearchTerm(e.target.value)}
            placeholder="Buscar por nome, tipo, ID ou emitente..."
            className="mb-3"
          />
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Nome</th>
                  <th className="border-b p-3 text-left">Tipo</th>
                  <th className="border-b p-3 text-left">Emitente</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(operationNatures as any[])
                  .filter((nature: any) =>
                    bulkNatureSearchTerm === "" ||
                    nature.nome?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase()) ||
                    nature.tipo?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase()) ||
                    String(nature.id).includes(bulkNatureSearchTerm) ||
                    nature.emitenteNome?.toLowerCase().includes(bulkNatureSearchTerm.toLowerCase())
                  )
                  .map((nature: any) => (
                    <tr key={nature.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-gray-500 font-mono text-sm">{nature.id}</td>
                      <td className="border-b p-3">{nature.nome}</td>
                      <td className="border-b p-3">{nature.tipo}</td>
                      <td className="border-b p-3 text-sm text-gray-600">{nature.emitenteNome || "-"}</td>
                      <td className="border-b p-3 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setBulkUpdateData({ ...bulkUpdateData, naturezaOperacaoId: nature.id });
                            setBulkNatureSearchTerm("");
                            setIsBulkNatureModalOpen(false);
                          }}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Emitente */}
      <Dialog open={isEmitenteModalOpen} onOpenChange={setIsEmitenteModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Selecionar Emitente</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Razão Social</th>
                  <th className="border-b p-3 text-left">CNPJ</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(emitentes as any[]).map((em: any) => (
                  <tr key={em.id} className="hover:bg-gray-50">
                    <td className="border-b p-3">{em.id}</td>
                    <td className="border-b p-3">{em.razaoSocial}</td>
                    <td className="border-b p-3">{em.cnpj}</td>
                    <td className="border-b p-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => {
                          setFormData({ ...formData, emitenteId: em.id, emitenteNome: em.razaoSocial });
                          setIsEmitenteModalOpen(false);
                        }}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        Selecionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
